import { IAccountRespSchema } from '@monetize/types/app';
import {
  FilterOptionType,
  FilterTypeEqual,
  FilterTypeOperator,
  RenderTableFilterOptionProps,
} from '@monetize/types/common';
import React from 'react';
import {
  MBox,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
  MSwitch,
  MText,
} from '../../../components/Monetize';
import { MDataTableFilter } from '../../../components/Monetize/DataTable';
import {
  ExternalFilterDef,
  useExternalFilters,
} from '../../../components/Monetize/DataTableNew/useExternalFilters';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { useInvoices } from '../../../hooks/AccountDetail';
import { getIsTrulyEmptyList } from '../../../utils';
import { InvoiceList } from '../../Invoices/InvoiceList';

const INVOICE_FILTER_DEFS: ExternalFilterDef[] = [
  {
    key: 'sent',
    columnHeader: 'Hide Sent Invoices',
  },
];

const renderSentInvoiceToggleOptionContent = ({
  filter,
  filterOption,
  handleFilterChange,
}: RenderTableFilterOptionProps) => {
  const internalFilter = filter as FilterTypeEqual;
  const { options } = filterOption;

  const falseValue = options?.falseValue ?? '';
  const trueValue = options?.trueValue ?? '';
  const isChecked = internalFilter?.value === trueValue;

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked ? trueValue : falseValue;
    handleFilterChange(newValue, filterOption);
  };

  return (
    <MFlex alignItems="center" my="2" justifyContent="space-between">
      <MText mr="4">Hide Sent Invoices</MText>
      <MSwitch size="md" isChecked={isChecked} onChange={handleToggleChange} />
    </MFlex>
  );
};

const AccountInvoices = ({
  account,
  billGroupId,
}: {
  account: IAccountRespSchema | IAccountRespSchema;
  billGroupId?: string;
}) => {
  const { id: accountId } = account;

  const {
    filters,
    setFilters,
    onResetFilter,
    setPager,
    pager,
    isLoading,
    invoices,
    searchTerm,
    setSearchTerm,
    isError,
    error,
    refetch,
  } = useInvoices(accountId, billGroupId);

  // External filter options: sent, amount, and modifyDate.
  // Status and invoicingDate filters are handled by DataTableNew column headers.
  const externalFilterOptions: FilterOptionType[] = [
    {
      title: '',
      key: 'sent',
      operator: FilterTypeOperator.TOGGLE,
      renderOptionContent: renderSentInvoiceToggleOptionContent,
      options: {
        falseValue: '',
        trueValue: 'false',
      },
    },
  ];

  const { extraActiveFilters, externalFilters, setExternalFilters } =
    useExternalFilters({
      filters,
      setFilters,
      pager,
      setPager,
      filterDefs: INVOICE_FILTER_DEFS,
    });

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading: isLoading,
    totalElements: invoices?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const filterComponentReset = React.useRef<any>(null);

  return (
    <MBox>
      <MPageHeader title="Invoices" hideContent={isTrulyEmptyList}>
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Invoice Number"
              value={searchTerm}
              count={invoices?.totalElements}
              mode="alwaysExpanded"
              onChange={setSearchTerm}
            />
            <RefreshTableButton refetch={refetch} />

            <MDataTableFilter
              filters={externalFilters}
              filterOptions={externalFilterOptions}
              setFilters={setExternalFilters}
              onResetFilter={onResetFilter}
              resetFilter={filterComponentReset}
            />
            <ExportEntityButton
              entity="invoices"
              filters={filters}
              searchKey="id"
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              internalFilters={[
                {
                  key: 'accountId',
                  operator: FilterTypeOperator.EQUAL,
                  value: accountId,
                },
              ]}
              applyInternalFiltersWithoutConfirmation
              getFilename={() => `invoices-${accountId}.csv`}
            />
          </MHStack>
        </MFlex>
      </MPageHeader>
      <InvoiceList
        filters={filters}
        setFilters={setFilters}
        onResetFilter={onResetFilter}
        setPager={setPager}
        pager={pager}
        isLoading={isLoading}
        // TODO: fix any type
        invoices={invoices as any}
        searchTerm={searchTerm}
        filterComponentReset={filterComponentReset}
        isError={isError}
        error={error}
        refetch={refetch}
        extraActiveFilters={extraActiveFilters}
      />
    </MBox>
  );
};

export default AccountInvoices;
